import {
  suggestionProvenance,
  takesAnswers,
  type AnswerSuggestion,
  type Channel,
} from '@cyberscore/contracts';
import { AlertTriangle, Check, ChevronRight, FileSearch, ShieldCheck, Sparkles, X } from 'lucide-react';
import { useState } from 'react';
import { ApiError } from '../../lib/api';
import { href } from '../../lib/router';
import { useSession } from '../../lib/session';
import { useToast } from '../../ui/Toasts';
import { useAssessment, useCanAnswer } from '../assessments/api';
import { ClaimsCard } from '../claims/ClaimsCard';
import { useAcceptTogether, useDecide, useDocuments, useSuggestions, useUpload } from './api';
import { DocumentRow, DropZone, Quote, uploadError } from './DocumentParts';
import { reviewGroups } from './files';

/**
 * The assessment's documents (task 40): upload evidence, see what each was read as, and decide what the
 * documents say: answers for unanswered questions, and conflicts where a document disagrees with an answer
 * or with another document. Nothing a document says is saved until a person accepts it (decision 0020).
 */
export function DocumentsPage({
  id,
  embedded = false,
}: {
  id: string;
  /** Inside Client 360's Documents tab: no crumbs or heading. */ embedded?: boolean;
}) {
  const detail = useAssessment(id);
  const docs = useDocuments(id);
  const suggestions = useSuggestions(id);
  const upload = useUpload(id);
  const toast = useToast();
  const canAnswer = useCanAnswer();

  if (detail.isPending || docs.isPending) return <div className="empty">Loading…</div>;
  if (detail.isError || docs.isError)
    return (
      <div className="empty">
        <strong>Could not load the documents</strong>
        {(detail.error ?? docs.error)?.message}
      </div>
    );

  const open = takesAnswers(detail.data.status) && canAnswer;
  const g = reviewGroups(suggestions.data ?? []);

  return (
    <>
      {!embedded && (<>
      <div className="crumbs">
        <a href={href.assessments()}>Assessments</a>
        <ChevronRight size={14} />
        <a href={href.assessment(id)}>{detail.data.customer_name}</a>
        <ChevronRight size={14} />
        Documents
      </div>
      <div className="page-head">
        <h1>Documents</h1>
        <p>
          Upload a policy schedule, a backup report or a security policy and we’ll read the answers it gives.
          Nothing is saved until you accept it.
        </p>
      </div>
      </>)}

      <div className="layout-side">
        <div className="stack">
          <ClaimsCard id={id} canChange={open && detail.data.status === 'in_progress'} />
          {suggestions.isPending ? (
            <section className="card pad">
              <p className="muted small">Loading what your documents say…</p>
            </section>
          ) : suggestions.isError ? (
            <section className="card pad">
              <div className="notice">Could not load what your documents say: {suggestions.error.message}</div>
            </section>
          ) : g.total === 0 ? (
            <section className="card pad">
              <div className="eyebrow">From your documents</div>
              <p className="muted small">
                {docs.data.length
                  ? 'Nothing to review. Answers read from your documents appear here for you to accept or decline.'
                  : 'Upload a document to have its answers read for you.'}
              </p>
            </section>
          ) : (
            <>
              {g.sure.length > 0 && <TakeTogether id={id} list={g.sure} open={open} />}
              {g.conflicts.length > 0 && (
                <section className="card pad" aria-labelledby="conflicts-h">
                  <h2 id="conflicts-h" className="section-title row gap">
                    <AlertTriangle size={17} className="warn-t" /> Where your documents disagree ({g.conflicts.length})
                  </h2>
                  <p className="muted small">Pick the answer that is right today. The other stays as it is.</p>
                  <div className="stack-sm">
                    {g.conflicts.map((group) => (
                      <Conflict key={group[0]!.question} id={id} group={group} open={open} />
                    ))}
                  </div>
                </section>
              )}
              {g.fills.length > 0 && (
                <section className="card pad" aria-labelledby="fills-h">
                  <h2 id="fills-h" className="section-title row gap">
                    <FileSearch size={17} /> Answers found ({g.fills.length})
                  </h2>
                  <ul className="review-list">
                    {g.fills.map((s) => (
                      <Suggestion key={s.id} id={id} s={s} open={open} />
                    ))}
                  </ul>
                </section>
              )}
              {g.confirms.length > 0 && (
                <section className="card pad" aria-labelledby="same-h">
                  <h2 id="same-h" className="section-title row gap">
                    <ShieldCheck size={17} className="ok-t" /> Your answers, backed by a document ({g.confirms.length})
                  </h2>
                  <VerifyNote />
                  <ul className="review-list">
                    {g.confirms.map((s) => (
                      <Suggestion key={s.id} id={id} s={s} open={open} />
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        <aside className="stack">
          <section className="card pad">
            <div className="eyebrow">Uploaded</div>
            {open && (
              <DropZone
                busy={upload.isPending}
                onFile={async (file) => {
                  try {
                    const r = await upload.mutateAsync({ file });
                    toast(r.duplicate ? `${file.name} was already uploaded` : `${file.name} uploaded. Reading it now.`, 'ok');
                  } catch (e) {
                    toast(uploadError(e), 'bad');
                  }
                }}
              />
            )}
            {docs.data.length ? (
              <ul className="doc-list mt">
                {docs.data.map((d) => (
                  <DocumentRow key={d.id} d={d} />
                ))}
              </ul>
            ) : (
              <p className="muted small mt">No documents yet.</p>
            )}
          </section>
        </aside>
      </div>
    </>
  ); // prettier-ignore
}

const CHANNEL_WORDS: Record<Channel, string> = {
  form: 'Given in the form',
  chat: 'Given in the chat',
  admin: 'Set by staff',
  document: 'Accepted from a document',
  integration: 'From a connected system',
  system: 'Filled in automatically',
};

function useDecision(id: string) {
  const decide = useDecide(id);
  const toast = useToast();
  return {
    pending: decide.isPending,
    run: async (s: AnswerSuggestion, to: 'accept' | 'decline') => {
      try {
        await decide.mutateAsync({ s, to });
        toast(to === 'accept' ? `Saved: ${s.label}` : 'Declined', 'ok');
      } catch (e) {
        toast(
          e instanceof ApiError && e.status === 409
            ? 'This changed since you loaded it (answered or decided elsewhere). Here is where it stands now.'
            : e instanceof Error
              ? e.message
              : 'Couldn’t save that',
          'bad',
        );
      }
    },
  };
}

/**
 * Sure, undisputed answers to optional questions, taken together in one click (task 106, decision 0074).
 * The list is there to check, folded; anything that needs a person's judgement is listed below one by one.
 */
function TakeTogether({ id, list, open }: { id: string; list: AnswerSuggestion[]; open: boolean }) {
  const take = useAcceptTogether(id);
  const toast = useToast();
  const [shown, setShown] = useState(false);
  const n = list.length;
  const docs = new Set(list.map((s) => s.document.id)).size;
  return (
    <section className="card pad take-together" aria-labelledby="sure-h">
      <h2 id="sure-h" className="section-title row gap">
        <Sparkles size={17} /> {n === 1 ? '1 answer' : `${n} answers`} we’re confident about
      </h2>
      <p className="muted small">
        Read clearly from {docs === 1 ? 'your document' : `${docs} documents`}, for questions that don’t affect the
        score limit, and no other document disagrees. Anything that needs a closer look is listed below.
      </p>
      <VerifyNote />
      <div className="row gap wrap mt">
        {open && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={take.isPending}
            onClick={async () => {
              try {
                const r = await take.mutateAsync(list.map((s) => s.id));
                toast(
                  r.left.length
                    ? `Saved ${r.accepted.length}. ${r.left.length} changed meanwhile and are listed below to check.`
                    : `Saved ${r.accepted.length} ${r.accepted.length === 1 ? 'answer' : 'answers'}`,
                  'ok',
                );
              } catch (e) {
                toast(e instanceof Error ? e.message : 'Couldn’t save those', 'bad');
              }
            }}
          >
            <Check size={15} /> {take.isPending ? 'Saving…' : n === 1 ? 'Use this answer' : `Use these ${n} answers`}
          </button>
        )}
        <button type="button" className="btn btn-ghost" aria-expanded={shown} onClick={() => setShown(!shown)}>
          {shown ? 'Hide them' : 'See them first'}
        </button>
      </div>
      {shown && (
        <ul className="review-list mt">
          {list.map((s) => (
            <Suggestion key={s.id} id={id} s={s} open={open} />
          ))}
        </ul>
      )}
    </section>
  ); // prettier-ignore
}

/** Whether accepting here counts as verifying (the insurer's staff) or only as read from the document. */
function useVerifies() {
  const { tenant } = useSession();
  return suggestionProvenance(tenant?.permissions ?? []) === 'DOCUMENT_VERIFIED';
}

function VerifyNote() {
  return (
    <p className="muted small">
      {useVerifies()
        ? 'Accepting marks the answer as verified by the document.'
        : 'Accepting records the answer as read from the document. Your insurer verifies it.'}
    </p>
  );
}

function Suggestion({ id, s, open }: { id: string; s: AnswerSuggestion; open: boolean }) {
  const d = useDecision(id);
  const verifies = useVerifies();
  return (
    <li className="review">
      <div className="qtext">
        {s.question_text}
        {s.kill_switch && <span className="badge">Affects your score limit</span>}
      </div>
      <div className="answer">{s.label}</div>
      <Quote s={s} />
      {open && (
        <div className="row gap mt">
          <button type="button" className="btn btn-sm btn-primary" disabled={d.pending} onClick={() => d.run(s, 'accept')}>
            <Check size={14} /> {s.kind === 'same' ? (verifies ? 'Mark as verified' : 'Confirm') : 'Use this answer'}
          </button>
          <button type="button" className="btn btn-sm btn-ghost" disabled={d.pending} onClick={() => d.run(s, 'decline')}>
            <X size={14} /> {s.kind === 'same' ? 'Dismiss' : 'Not right'}
          </button>
        </div>
      )}
    </li>
  ); // prettier-ignore
}

/**
 * One question where documents disagree with the saved answer or with each other: the current answer and
 * each document's, side by side. Choosing a document saves its answer; keeping the current one declines
 * every document's.
 */
function Conflict({ id, group, open }: { id: string; group: AnswerSuggestion[]; open: boolean }) {
  const d = useDecision(id);
  const first = group[0]!;
  const keep = async () => {
    for (const s of group) await d.run(s, 'decline');
  };
  return (
    <div className="conflict" role="group" aria-label={first.question_text}>
      <div className="qtext">
        {first.question_text}
        {first.kill_switch && <span className="badge">Affects your score limit</span>}
      </div>
      <div className="conflict-options">
        {first.current && (
          <div className="conflict-option current">
            <div className="option-label">Your answer now</div>
            <div className="answer">{first.current.label}</div>
            <div className="muted small">{CHANNEL_WORDS[first.current.channel]}</div>
            {open && (
              <button type="button" className="btn btn-sm mt" disabled={d.pending} onClick={keep}>
                Keep this answer
              </button>
            )}
          </div>
        )}
        {group.map((s) => (
          <div key={s.id} className="conflict-option">
            <div className="option-label">{s.document.file_name} says</div>
            <div className="answer">{s.label}</div>
            <Quote s={s} />
            {open && (
              <button type="button" className="btn btn-sm btn-primary mt" disabled={d.pending} onClick={() => d.run(s, 'accept')}>
                Use this answer
              </button>
            )}
          </div>
        ))}
      </div>
      {!first.current && open && (
        <button type="button" className="btn btn-sm btn-ghost" disabled={d.pending} onClick={keep}>
          None of these is right
        </button>
      )}
    </div>
  ); // prettier-ignore
}
